import React, { useMemo } from "react";
import { TrendingUp, Calendar } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { format, parseISO, eachDayOfInterval, startOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import type { CouponAnalysis } from "@/types/coupon";

interface SalesChartProps {
  couponAnalysis: CouponAnalysis | null;
  dateRange: { from: Date | undefined; to: Date | undefined };
  loading?: boolean;
}

const chartConfig = {
  sales: {
    label: "Vendas",
    color: "hsl(24.6 95% 53.1%)",
  },
} satisfies ChartConfig;

export const SalesChart: React.FC<SalesChartProps> = ({ couponAnalysis, dateRange, loading }) => {
  const chartData = useMemo(() => {
    if (!couponAnalysis || !dateRange.from || !dateRange.to) return [];

    const days = eachDayOfInterval({
      start: startOfDay(dateRange.from),
      end: startOfDay(dateRange.to),
    });

    const dailySales = new Map<string, { total: number; count: number; completed: number }>();
    days.forEach((day) => {
      dailySales.set(format(day, "yyyy-MM-dd"), { total: 0, count: 0, completed: 0 });
    });

    couponAnalysis.orders.all.forEach((order) => {
      try {
        const orderDate = startOfDay(parseISO(order.date_created));
        const dateKey = format(orderDate, "yyyy-MM-dd");
        if (dailySales.has(dateKey)) {
          const current = dailySales.get(dateKey)!;
          const isCompleted = order.status === "completed";
          dailySales.set(dateKey, {
            total: current.total + (isCompleted ? Number(order.total) : 0),
            count: current.count + 1,
            completed: current.completed + (isCompleted ? 1 : 0),
          });
        }
      } catch {
        // skip orders with invalid dates
      }
    });

    return Array.from(dailySales.entries())
      .map(([date, data]) => ({
        date,
        sales: data.total,
        orders: data.completed,
        label: format(parseISO(date), "dd/MMM", { locale: ptBR }),
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [couponAnalysis, dateRange.from, dateRange.to]);

  if (loading && !couponAnalysis) {
    return (
      <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500 border-white/10 bg-white/[0.03] backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex flex-col justify-end gap-1">
            <div className="flex items-end gap-2 h-full">
              {[40, 65, 30, 80, 55, 70, 45, 90, 60, 35, 75, 50].map((h, i) => (
                <div
                  key={i}
                  className="flex-1 bg-muted rounded-t animate-pulse"
                  style={{ height: `${h}%`, animationDelay: `${i * 100}ms` }}
                />
              ))}
            </div>
            <div className="flex gap-2">
              {[...Array(12)].map((_, i) => (
                <div key={i} className="flex-1 h-3 bg-muted rounded animate-pulse" />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500 border-white/10 bg-white/[0.03] backdrop-blur-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Performance
            </CardTitle>
          </div>
          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {dateRange.from &&
                dateRange.to &&
                (() => {
                  const fromDate = new Date(dateRange.from!.getTime() - dateRange.from!.getTimezoneOffset() * 60000);
                  const toDate = new Date(dateRange.to!.getTime() - dateRange.to!.getTimezoneOffset() * 60000);
                  return `${format(fromDate, "dd/MMM", { locale: ptBR })} - ${format(toDate, "dd/MMM", { locale: ptBR })}`;
                })()}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {chartData.length > 0 && couponAnalysis && couponAnalysis.orders.completed.length > 0 ? (
          <ChartContainer config={chartConfig} className="h-[300px] w-full">
            <AreaChart
              accessibilityLayer
              data={chartData}
              margin={{ left: 12, right: 12, top: 12, bottom: 12 }}
            >
              <defs>
                <linearGradient id="fillSales" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--color-sales)" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="var(--color-sales)" stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} minTickGap={32} />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value) => `R$${value.toFixed(0)}`}
              />
              <ChartTooltip
                cursor={false}
                content={
                  <ChartTooltipContent
                    labelFormatter={(value, payload) => {
                      if (payload && payload[0]) {
                        const data = payload[0].payload;
                        return format(parseISO(data.date), "dd 'de' MMMM", { locale: ptBR });
                      }
                      return value;
                    }}
                    formatter={(value) => {
                      const formatted = Number(value).toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      });
                      return [formatted, "em vendas"];
                    }}
                  />
                }
              />
              <Area
                dataKey="sales"
                type="monotone"
                fill="url(#fillSales)"
                fillOpacity={0.4}
                stroke="var(--color-sales)"
                strokeWidth={2}
              />
            </AreaChart>
          </ChartContainer>
        ) : (
          <div className="text-center py-12">
            <TrendingUp className="h-16 w-16 text-muted-foreground/40 mx-auto mb-4" />
            <h3 className="font-semibold text-lg mb-2">Seu grafico aparecera aqui</h3>
            <p className="text-muted-foreground mb-4">
              Acompanhe aqui seus melhores dias de vendas, visualmente.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
